import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';
import { NgkitSearchComponent } from '@wtw/angularkit/ngkit-search';
import { forkJoin } from 'rxjs';
import { FgaPickerPanelComponent } from '../fga-picker-panel/fga-picker-panel.component';
import { FgaTableComponent } from '../fga-table/fga-table.component';
import { EllipsisTitleDirective } from '../../directives/ellipsis-title.directive';
import { Auth0OrganisationService } from '../../services/auth0-org.service';
import { FgaApiService } from '../../services/fga-api.service';
import { GroupStateService } from '../../services/group-state.service';
import { LoggerService } from '../../services/logger.service';
import { M2mClientService } from '../../services/m2m-client.service';
import { UserEnrichmentService } from '../../services/user-enrichment.service';
import { compareByName, sortByLabel } from '../../utils/sorting';
import { ENTITY_TYPE, MEMBERSHIP_MODE } from '../../models/entity.model';
import { EnrichedUser } from '../../models/auth0-user.model';
import { FgaGroup, isScimManagedGroupId } from '../../models/group.model';
import { FgaUser } from '../../models/user.model';
import { isM2MClientUserId, extractM2MClientId } from '../../models/fga-constants';
import { parseUserId, parseGroupId } from '../../models';
import { PickerConfig, PickerMode, PickerItem, PickerSavePayload } from '../fga-picker-panel/fga-picker-panel.model';
import { TabKey, TableRow } from '../fga-table/fga-table.model';
import {
  GROUP_VIEW_MEMBER_FILTER,
  GROUP_VIEW_OWNER_FILTER,
  GROUP_VIEW_TAB_VIEWS,
  GroupViewMemberFilter,
  GroupViewOwnerFilter,
  GroupViewTypeFilter,
  matchesTypeFilter
} from './fga-group-view.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonModule,
    CommonModule,
    EllipsisTitleDirective,
    FgaPickerPanelComponent,
    FgaTableComponent,
    FormsModule,
    NgkitSearchComponent,
    SelectButton
  ],
  selector: 'fga-group-view',
  styleUrls: ['./fga-group-view.component.scss'],
  templateUrl: './fga-group-view.component.html',
})
export class FgaGroupViewComponent implements OnChanges {
  @Input() group: FgaGroup | null = null;

  @Output() membershipEnrichmentLoading = new EventEmitter<boolean>();

  private readonly fgaApi = inject(FgaApiService);
  private readonly enrichment = inject(UserEnrichmentService);
  private readonly groupState = inject(GroupStateService);
  private readonly m2mClientService = inject(M2mClientService);
  private readonly auth0Org = inject(Auth0OrganisationService);
  private readonly logger = inject(LoggerService);

  pickerOpen = false;
  activePicker: PickerMode | null = null;
  pickerConfig: PickerConfig | null = null;

  protected readonly activeTab = signal<TabKey>(MEMBERSHIP_MODE.MEMBERS);
  protected readonly filterText = signal<string>('');
  protected readonly memberTypeFilter = signal<GroupViewMemberFilter>(GROUP_VIEW_MEMBER_FILTER.ALL);
  protected readonly ownerTypeFilter = signal<GroupViewOwnerFilter>(GROUP_VIEW_OWNER_FILTER.ALL);
  protected readonly owners = signal<FgaUser[]>([]);
  protected readonly members = signal<FgaUser[]>([]);
  protected readonly ownersLoading = signal(false);
  protected readonly membersLoading = signal(false);
  protected readonly enrichmentLoading = signal(false);
  protected readonly orgMembersEnrichmentLoading = signal(false);
  protected readonly isLoading = computed(
    () =>
      this.ownersLoading() ||
      this.membersLoading() ||
      this.enrichmentLoading() ||
      this.ownerGroupsLoading() ||
      this.orgGroupsLoading() ||
      this.orgMembersLoading() ||
      this.orgMembersEnrichmentLoading());

  // Organisation members for picker panel
  protected readonly orgMembers = signal<string[]>([]);
  protected readonly orgMembersLoading = signal(false);

  // Organisation groups and owner groups for owners_groups picker
  protected readonly orgGroups = signal<string[]>([]);
  protected readonly orgGroupsLoading = signal(false);
  protected readonly ownerGroups = signal<string[]>([]);
  protected readonly ownerGroupsLoading = signal(false);

  /** Membership of a SCIM-provisioned group is owned by the source IdP, so it is read-only here. */
  protected readonly scimManaged = signal(false);

  /** Full, unfiltered rows for the active tab. */
  protected readonly activeAllRows = computed<TableRow[]>(() =>
    this.activeTab() === MEMBERSHIP_MODE.MEMBERS ? this.membersRows() : this.ownersRows()
  );

  protected readonly tabView = computed(() => GROUP_VIEW_TAB_VIEWS[this.activeTab()]);

  protected readonly typeFilterValue = computed<GroupViewTypeFilter>(() =>
    this.activeTab() === MEMBERSHIP_MODE.MEMBERS ? this.memberTypeFilter() : this.ownerTypeFilter()
  );

  /** Rows of the active tab narrowed by the identity-type filter, before the search term. */
  protected readonly typeFilteredRows = computed<TableRow[]>(() => {
    const typeFilter = this.typeFilterValue();
    return this.activeAllRows().filter((row) => matchesTypeFilter(row, typeFilter));
  });

  protected readonly activeRows = computed<TableRow[]>(() => {
    const term = this.filterText().trim().toLowerCase();
    const rows = this.typeFilteredRows();

    if (!term) {
      return rows;
    }

    return rows.filter(
      (row) =>
        (row.name ?? '').toLowerCase().includes(term) ||
        (row.email ?? '').toLowerCase().includes(term)
    );
  });

  protected readonly filterPlaceholder = computed(() => this.tabView().searchPlaceholder);

  readonly ownersRows = computed<TableRow[]>(() => {
    // Track M2M client count to trigger re-computation when M2M data loads
    void this.m2mClientService.clientCount();

    // Map user owners (human or M2M)
    const userOwners: TableRow[] = this.owners().map((u) => {
      if (isM2MClientUserId(u.id)) {
        const clientId = extractM2MClientId(u.id);
        const client = this.m2mClientService.getClientById(clientId);
        if (client) {
          return {
            id: u.id,
            name: client.name,
            email: undefined,
            type: ENTITY_TYPE.M2M,
          };
        }
      }
      return {
        id: u.id,
        name: u.name,
        email: u.email,
        type: ENTITY_TYPE.USER,
      };
    });

    const groupOwners: TableRow[] = this.ownerGroups().map((groupId) => ({
      id: groupId,
      name: this.resolveGroupLabel(groupId),
      type: ENTITY_TYPE.GROUP,
    }));
    
    // Combine both user and group owners, sorted by best available display value
    return [...userOwners, ...groupOwners].sort((a, b) =>
      compareByName({ name: a.email ?? a.name }, { name: b.email ?? b.name })
    );
  });

  readonly membersRows = computed<TableRow[]>(() => {
    // Track M2M client count to trigger re-computation when M2M data loads
    void this.m2mClientService.clientCount();

    return this.members()
      .map((u) => {
        if (isM2MClientUserId(u.id)) {
          const clientId = extractM2MClientId(u.id);
          const client = this.m2mClientService.getClientById(clientId);
          if (client) {
            return {
              id: u.id,
              name: client.name,
              email: undefined,
              type: ENTITY_TYPE.M2M,
            };
          }
        }
        return {
          id: u.id,
          name: u.name,
          email: u.email,
          type: ENTITY_TYPE.USER,
        };
      })
      .sort((a, b) =>
        compareByName({ name: a.email ?? a.name }, { name: b.email ?? b.name })
      );
  });

  // Computed picker items: org members with selected state based on current group members
  readonly memberPickerItems = computed<PickerItem[]>(() => {
    // Track cache size to trigger re-computation when enrichment completes
    void this.enrichment.cacheSize();
    void this.m2mClientService.clientCount();

    const currentMemberIds = new Set(this.members().map(m => m.id));
    const items = this.orgMembers().map(userId => {
      // Check if this is a known M2M client
      if (isM2MClientUserId(userId)) {
        const clientId = extractM2MClientId(userId);
        const client = this.m2mClientService.getClientById(clientId);
        if (client) {
          return {
            id: userId,
            label: client.name,
            email: undefined,
            type: ENTITY_TYPE.M2M,
            selected: currentMemberIds.has(userId),
          };
        }
      }

      // Use enrichment service to get display value (email/upn) if available
      // Pass full userId (with user: prefix) as that's how the cache is keyed
      const displayName = this.enrichment.getDisplayName(userId);
      const displayEmail = this.enrichment.getDisplayEmail(userId);

      return {
        id: userId,
        label: displayName,
        email: displayEmail,
        type: ENTITY_TYPE.USER,
        selected: currentMemberIds.has(userId),
      };
    });
    return sortByLabel(items);
  });

  // Computed picker items: eligible org members with selected state based on current group owners.
  // Service (M2M) identities are excluded: group ownership must be held by accountable
  // human users or groups, never by service identities.
  readonly ownerPickerItems = computed<PickerItem[]>(() => {
    // Track cache size to trigger re-computation when enrichment completes
    void this.enrichment.cacheSize();
    // Track M2M client count to re-evaluate service-user exclusion once clients load
    void this.m2mClientService.clientCount();

    const currentOwnerIds = new Set(this.owners().map(o => o.id));
    const items = this.orgMembers()
      .filter(userId => !this.isServiceUser(userId))
      .map(userId => {
        const displayName = this.enrichment.getDisplayName(userId);
        const displayEmail = this.enrichment.getDisplayEmail(userId);

        return {
          id: userId,
          label: displayName,
          email: displayEmail,
          type: ENTITY_TYPE.USER,
          selected: currentOwnerIds.has(userId),
        };
      });
    return sortByLabel(items);
  });

  // Computed picker items: org groups with selected state based on current owner groups
  // Excludes the current group (a group cannot be its own owner)
  readonly ownerGroupPickerItems = computed<PickerItem[]>(() => {
    const currentGroupId = this.group?.id;
    const currentOwnerGroupIds = new Set(this.ownerGroups());
    
    const items = this.orgGroups()
      .filter(groupId => groupId !== currentGroupId) // Exclude self
      .map(groupId => ({
        id: groupId,
        label: this.resolveGroupLabel(groupId),
        type: ENTITY_TYPE.GROUP,
        selected: currentOwnerGroupIds.has(groupId),
      }));
    return sortByLabel(items);
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group']) {
      this.scimManaged.set(isScimManagedGroupId(this.group?.id));

      if (this.scimManaged()) {
        this.activeTab.set(MEMBERSHIP_MODE.MEMBERS);
      }

      this.resetFilters();
      this.loadUsersForGroup();
    }
  }

  private resolveGroupLabel(groupId: string): string {
    const parsedName = this.parseGroupName(groupId);
    if (parsedName === null) {
      return groupId;
    }

    const stateName = this.groupState.groups().find(group => group.id === groupId)?.name;
    if (stateName && stateName !== parsedName) {
      return stateName;
    }

    return this.auth0Org.groupNames()[parsedName] ?? stateName ?? parsedName;
  }

  private parseGroupName(groupId: string): string | null {
    try {
      return parseGroupId(groupId).name;
    } catch {
      return null;
    }
  }

  private async resolveScimOwnerGroupNames(groupIds: string[]): Promise<void> {
    const scimIds = groupIds
      .map(groupId => this.parseGroupName(groupId))
      .filter((name): name is string => name !== null && isScimManagedGroupId(name));

    if (scimIds.length === 0) {
      return;
    }

    await this.auth0Org.resolveGroupNames(scimIds);
  }

  private isServiceUser(userId: string): boolean {
    if (!isM2MClientUserId(userId)) {
      return false;
    }
    const clientId = extractM2MClientId(userId);
    return !!this.m2mClientService.getClientById(clientId);
  }

  setTab(tab: TabKey): void {
    if (this.scimManaged() && tab === MEMBERSHIP_MODE.OWNERS) {
      return;
    }

    this.activeTab.set(tab);
    this.resetFilters();
  }

  onTypeFilterChange(value: GroupViewTypeFilter): void {
    if (this.activeTab() === MEMBERSHIP_MODE.MEMBERS) {
      this.memberTypeFilter.set((value ?? GROUP_VIEW_MEMBER_FILTER.ALL) as GroupViewMemberFilter);
      return;
    }

    this.ownerTypeFilter.set((value ?? GROUP_VIEW_OWNER_FILTER.ALL) as GroupViewOwnerFilter);
  }

  private resetFilters(): void {
    this.filterText.set('');
    this.memberTypeFilter.set(GROUP_VIEW_MEMBER_FILTER.ALL);
    this.ownerTypeFilter.set(GROUP_VIEW_OWNER_FILTER.ALL);
  }

  onFilter(value: string): void {
    this.filterText.set(value ?? '');
  }

  clearFilter(): void {
    this.filterText.set('');
  }

  openPicker(pickerMode: PickerMode): void {
    this.activePicker = pickerMode;
    this.refreshPickerConfig();
    this.pickerOpen = true;
  }

  closePicker(): void {
    this.pickerOpen = false;
    this.activePicker = null;
    this.activePicker = null;
  }

  onManageMembers(): void {
    if (this.scimManaged()) {
      return;
    }

    this.openPicker(MEMBERSHIP_MODE.MEMBERS);
    this.loadOrganisationMembers();
  }

  onManageOwners(): void {
    if (this.scimManaged()) {
      return;
    }

    this.openPicker(MEMBERSHIP_MODE.OWNERS);
    this.loadOrganisationMembers();
    this.loadOrganisationGroups();
    this.loadGroupOwnerGroups();
  }

  onPickerSave(payload: PickerSavePayload): void {
    if (!this.group?.id) {
      return;
    }

    if (this.scimManaged()) {
      this.closePicker();
      return;
    }

    if (payload.mode === MEMBERSHIP_MODE.MEMBERS) {
      this.saveMembers(payload);
      return;
    }
  
    this.saveOwners(payload);
  }

  private refreshPickerConfig(): void {
    if (!this.activePicker) {
      this.pickerConfig = null;
      return;
    }

    if (this.activePicker === MEMBERSHIP_MODE.MEMBERS) {
      this.pickerConfig = {
        mode: MEMBERSHIP_MODE.MEMBERS,
        lists: {
          members: this.memberPickerItems(),
        },
      };
      return;
    }

    this.pickerConfig = {
      mode: MEMBERSHIP_MODE.OWNERS,
      lists: {
        ownerUsers: this.ownerPickerItems(),
        ownerGroups: this.ownerGroupPickerItems(),
      },
    };
  }

  private saveMembers(payload: PickerSavePayload): void {
    const groupId = this.group?.id;

    if (!groupId) {
      return;
    }
  
    const stagedMembers = payload.lists.members ?? [];
    const selectedIds = new Set(
      stagedMembers.filter(item => item.selected).map(item => item.id)
    );
    const currentIds = new Set(this.members().map(member => member.id));
    const idsToAdd = [...selectedIds].filter(id => !currentIds.has(id));
    const idsToRemove = [...currentIds].filter(id => !selectedIds.has(id));
  
    if (idsToAdd.length === 0 && idsToRemove.length === 0) {
      this.closePicker();
      return;
    }
  
    const addRequests = idsToAdd.map(id =>
      this.fgaApi.addUserToGroup(groupId, id)
    );
    const removeRequests = idsToRemove.map(id =>
      this.fgaApi.removeUserFromGroup(groupId, id)
    );

    forkJoin([...addRequests, ...removeRequests]).subscribe({
      next: () => {
        const nextMembers = stagedMembers
          .filter(item => item.selected)
          .map(item => {
            const existing = this.members().find(member => member.id === item.id);
            if (existing) {
              return existing;
            }
            const parsed = parseUserId(item.id);
            return {
              id: item.id,
              name: item.label || parsed.userId,
            } as FgaUser;
          });
        this.members.set(nextMembers);
        idsToAdd.forEach(id => this.enrichNewlyAddedUser(id, this.members));
        this.closePicker();
      },
      error: (err) => {
        this.logger.error('FgaGroupView', 'Failed to save members changes', { error: err });
      },
    });
  }

  private saveOwners(payload: PickerSavePayload): void {
    const groupId = this.group?.id;

    if (!groupId) {
      return;
    }

    const stagedOwnerUsers = payload.lists.ownerUsers ?? [];
    const stagedOwnerGroups = payload.lists.ownerGroups ?? [];
    const selectedUserIds = new Set(
      stagedOwnerUsers.filter(item => item.selected).map(item => item.id)
    );
    const selectedGroupIds = new Set(
      stagedOwnerGroups.filter(item => item.selected).map(item => item.id)
    );
    const currentOwnerUserIds = new Set(this.owners().map(owner => owner.id));
    const currentOwnerGroupIds = new Set(this.ownerGroups());
    const userIdsToAdd = [...selectedUserIds]
      .filter(id => !currentOwnerUserIds.has(id))
      .filter(id => !this.isServiceUser(id));
    const userIdsToRemove = [...currentOwnerUserIds].filter(id => !selectedUserIds.has(id));
    const groupIdsToAdd = [...selectedGroupIds].filter(id => !currentOwnerGroupIds.has(id));
    const groupIdsToRemove = [...currentOwnerGroupIds].filter(id => !selectedGroupIds.has(id));

    if (
      userIdsToAdd.length === 0 &&
      userIdsToRemove.length === 0 &&
      groupIdsToAdd.length === 0 &&
      groupIdsToRemove.length === 0
    ) {
      this.closePicker();
      return;
    }

    const addOwnerUserRequests = userIdsToAdd.map(id =>
      this.fgaApi.addOwnerToGroup(groupId, id)
    );
    const removeOwnerUserRequests = userIdsToRemove.map(id =>
      this.fgaApi.removeOwnerFromGroup(groupId, id)
    );
    const addOwnerGroupRequests = groupIdsToAdd.map(id =>
      this.fgaApi.addOwnerGroupToGroup(groupId, id)
    );
    const removeOwnerGroupRequests = groupIdsToRemove.map(id =>
      this.fgaApi.removeOwnerGroupFromGroup(groupId, id)
    );

    forkJoin([
      ...addOwnerUserRequests,
      ...removeOwnerUserRequests,
      ...addOwnerGroupRequests,
      ...removeOwnerGroupRequests,
    ]).subscribe({
      next: () => {
        const nextOwners = stagedOwnerUsers
          .filter(item => item.selected)
          .map(item => {
            const existing = this.owners().find(owner => owner.id === item.id);
            if (existing) {
              return existing;
            }
            const parsed = parseUserId(item.id);
            return {
              id: item.id,
              name: item.label || parsed.userId,
            } as FgaUser;
          });
        const nextOwnerGroups = stagedOwnerGroups
          .filter(item => item.selected)
          .map(item => item.id);
        this.owners.set(nextOwners);
        this.ownerGroups.set(nextOwnerGroups);
        void this.resolveScimOwnerGroupNames(nextOwnerGroups);
        userIdsToAdd.forEach(id => this.enrichNewlyAddedUser(id, this.owners));
        this.closePicker();
      },
      error: (err) => {
        this.logger.error('FgaGroupView', 'Failed to save owners changes', { error: err });
      },
    });
  }

  onRemove(row: TableRow): void {
    if (!this.group?.id) return;
    if (this.scimManaged()) return;

    const groupId = this.group.id;

    if (this.activeTab() === MEMBERSHIP_MODE.OWNERS) {
      // Remove from owners
      if (row.type === ENTITY_TYPE.USER || row.type === ENTITY_TYPE.M2M) {
        this.fgaApi.removeOwnerFromGroup(groupId, row.id).subscribe({
          next: () => {
            this.owners.update(current => current.filter(o => o.id !== row.id));
          },
          error: (err) => {
            this.logger.error('FgaGroupView', 'Failed to remove owner from group', { error: err });
            // TODO: Show error notification
          },
        });
      } else if (row.type === ENTITY_TYPE.GROUP) {
        this.fgaApi.removeOwnerGroupFromGroup(groupId, row.id).subscribe({
          next: () => {
            this.ownerGroups.update(current => current.filter(g => g !== row.id));
          },
          error: (err) => {
            this.logger.error('FgaGroupView', 'Failed to remove owner group from group', { error: err });
            // TODO: Show error notification
          },
        });
      }
    } else if (this.activeTab() === MEMBERSHIP_MODE.MEMBERS) {
      // Remove from members
      if (row.type === ENTITY_TYPE.USER || row.type === ENTITY_TYPE.M2M) {
        this.fgaApi.removeUserFromGroup(groupId, row.id).subscribe({
          next: () => {
            this.members.update(current => current.filter(m => m.id !== row.id));
          },
          error: (err) => {
            this.logger.error('FgaGroupView', 'Failed to remove user from group', { error: err });
            // TODO: Show error notification
          },
        });
      }
    }
  }

  // ---- Load organisation members for picker ----
  private loadOrganisationMembers(): void {
    const orgId = this.group?.organisationId;
    if (!orgId) {
      this.orgMembers.set([]);
      return;
    }

    this.orgMembersLoading.set(true);
    this.fgaApi.getOrganisationMembers(orgId).subscribe({
      next: (userIds: string[]) => {
        this.orgMembers.set(userIds);
        this.orgMembersLoading.set(false);
        // Enrich org members for display
        this.enrichOrgMembersInBackground(userIds);
        this.refreshPickerConfig();
      },
      error: (err) => {
        this.logger.error('FgaGroupView', 'Failed to load organisation members', { error: err });
        this.orgMembers.set([]);
        this.orgMembersLoading.set(false);
      },
    });
  }

  // ---- Load organisation groups for owners_groups picker ----
  private loadOrganisationGroups(): void {
    const orgId = this.group?.organisationId;
    if (!orgId) {
      this.orgGroups.set([]);
      return;
    }

    this.orgGroupsLoading.set(true);
    this.fgaApi.getGroups(orgId).subscribe({
      next: (response) => {
        this.orgGroups.set(response.groups ?? []);
        this.orgGroupsLoading.set(false);
        this.refreshPickerConfig();
      },
      error: (err) => {
        this.logger.error('FgaGroupView', 'Failed to load organisation groups', { error: err });
        this.orgGroups.set([]);
        this.orgGroupsLoading.set(false);
      },
    });
  }

  // ---- Load groups that are owners of the current group ----
  private loadGroupOwnerGroups(): void {
    const groupId = this.group?.id;
    if (!groupId) {
      this.ownerGroups.set([]);
      return;
    }

    this.ownerGroupsLoading.set(true);
    this.fgaApi.getGroupOwnerGroups(groupId).subscribe({
      next: (response) => {
        const ownerGroups = response.groups ?? [];
        this.ownerGroups.set(ownerGroups);
        this.ownerGroupsLoading.set(false);
        void this.resolveScimOwnerGroupNames(ownerGroups);
        this.refreshPickerConfig();
      },
      error: (err) => {
        this.logger.error('FgaGroupView', 'Failed to load group owner groups', { error: err });
        this.ownerGroups.set([]);
        this.ownerGroupsLoading.set(false);
      },
    });
  }

  /** Enriches a single newly-added user and updates the given list signal. */
  private async enrichNewlyAddedUser(
    userId: string,
    listSignal: ReturnType<typeof signal<FgaUser[]>>
  ): Promise<void> {
    // M2M clients are enriched via M2M client service (already cached)
    if (isM2MClientUserId(userId)) {
      const clientId = extractM2MClientId(userId);
      if (this.m2mClientService.getClientById(clientId)) return;
    }

    if (!this.enrichment.enabled()) return;
    this.membershipEnrichmentLoading.emit(true);
    try {
      const results = await this.enrichment.enrichUsers([userId]);
      const enriched = results?.[0];
      if (enriched) {
        listSignal.update(current =>
          current.map(u =>
            u.id === userId
              ? {
                  ...u,
                  ...(enriched.name !== undefined && { name: enriched.name }),
                  ...(enriched.email !== undefined && { email: enriched.email }),
                }
              : u
          )
        );
      }
    } catch (err) {
      this.logger.error('FgaGroupView', 'Failed to enrich newly added user', { error: err });
    } finally {
      this.membershipEnrichmentLoading.emit(false);
    }
  }

  private async enrichOrgMembersInBackground(userIds: string[]): Promise<void> {
    // Filter out known M2M client IDs — they use M2M client service
    const humanUserIds = userIds.filter(id => {
      if (isM2MClientUserId(id)) {
        const clientId = extractM2MClientId(id);
        return !this.m2mClientService.getClientById(clientId);
      }
      return true;
    });
    if (humanUserIds.length === 0 || !this.enrichment.enabled()) return;

    this.orgMembersEnrichmentLoading.set(true);
    try {
      // Enrich users - this populates the enrichment cache
      // memberPickerItems re-computes automatically via enrichment.cacheSize() dependency
      await this.enrichment.enrichUsers(humanUserIds);
    } catch (err) {
      this.logger.error('FgaGroupView', 'Failed to enrich org members', { error: err });
    } finally {
      this.orgMembersEnrichmentLoading.set(false);
    }
  }

  // ---- Data loading (ported from fga-groups-details) ----
  private loadUsersForGroup(): void {
    const g = this.group;

    // Always clear state immediately when the group input changes so that stale
    // data from the previously-selected group is never visible while the new
    // group's data is loading.  This also prevents race conditions where an
    // in-flight HTTP response for the OLD group arrives after the new group has
    // already been selected.
    this.owners.set([]);
    this.members.set([]);
    this.ownerGroups.set([]);
    this.ownersLoading.set(false);
    this.membersLoading.set(false);
    this.ownerGroupsLoading.set(false);

    if (!g?.id) {
      return;
    }

    // Capture the group ID at request time so that we can discard responses
    // that arrive after the user has navigated to a different group (stale
    // response guard).
    const requestedGroupId = g.id;

    if (!this.scimManaged()) {
      this.ownersLoading.set(true);

      this.fgaApi.getGroupOwners(requestedGroupId).subscribe({
        next: (res: { users?: string[] }) => {
          if (this.group?.id !== requestedGroupId) return;
          const users = this.mapUsers(res?.users ?? []);
          this.owners.set(users);
          this.ownersLoading.set(false);
          this.enrichUsersInBackground(users);
        },
        error: () => {
          if (this.group?.id !== requestedGroupId) return;
          this.owners.set([]);
          this.ownersLoading.set(false);
        },
      });

      this.ownerGroupsLoading.set(true);

      this.fgaApi.getGroupOwnerGroups(requestedGroupId).subscribe({
        next: (response) => {
          if (this.group?.id !== requestedGroupId) return;
          const ownerGroups = response.groups ?? [];
          this.ownerGroups.set(ownerGroups);
          this.ownerGroupsLoading.set(false);
          void this.resolveScimOwnerGroupNames(ownerGroups);
        },
        error: (err) => {
          if (this.group?.id !== requestedGroupId) return;
          this.logger.error('FgaGroupView', 'Failed to load group owner groups', { error: err });
          this.ownerGroups.set([]);
          this.ownerGroupsLoading.set(false);
        },
      });
    }

    // ---- MEMBERS ----
    this.membersLoading.set(true);
    this.fgaApi.getGroupMembers(requestedGroupId).subscribe({
      next: (res: { users?: string[] }) => {
        if (this.group?.id !== requestedGroupId) return; // stale
        const users = this.mapUsers(res?.users ?? []);
        this.members.set(users);
        this.membersLoading.set(false);
        // background enrichment
        this.enrichUsersInBackground(users);
      },
      error: () => {
        if (this.group?.id !== requestedGroupId) return; // stale
        this.members.set([]);
        this.membersLoading.set(false);
      },
    });
  }

  private async enrichUsersInBackground(users: FgaUser[]): Promise<void> {
    if (users.length === 0 || !this.enrichment.enabled()) return;

    // Filter out known M2M client IDs — they use M2M client service, not Auth0 enrichment
    const humanUsers = users.filter(u => {
      if (isM2MClientUserId(u.id)) {
        const clientId = extractM2MClientId(u.id);
        return !this.m2mClientService.getClientById(clientId);
      }
      return true;
    });
    if (humanUsers.length === 0) return;

    this.enrichmentLoading.set(true);
    try {
      const enrichedList = await this.enrichment.enrichUsers(humanUsers.map((u) => u.id));
      const enrichMap = new Map<string, EnrichedUser>(
        (enrichedList ?? []).map((e) => [e.fgaUserId, e])
      );
      this.owners.update((current) =>
        current.map((u) => {
          const enriched = enrichMap.get(u.id);
          return {
            ...u,
            ...(enriched?.name !== undefined && { name: enriched.name }),
            ...(enriched?.email !== undefined && { email: enriched.email }),
          };
        })
      );
      this.members.update((current) =>
        current.map((u) => {
          const enriched = enrichMap.get(u.id);
          return {
            ...u,
            ...(enriched?.name !== undefined && { name: enriched.name }),
            ...(enriched?.email !== undefined && { email: enriched.email }),
          };
        })
      );
    } finally {
      this.enrichmentLoading.set(false);
    }
  }

  private mapUsers(userIds: string[]): FgaUser[] {
    return (userIds ?? []).map((fullId) => {
      const parsed = parseUserId(fullId);
      return {
        id: fullId,
        name: parsed.userId,
        email: undefined,
      } as FgaUser;
    });
  }
}
